import { Box } from '@mui/material'
import { FormikProps, useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslations } from '~/src/hooks/useTranslations'
import { LoginTypeSelectionFormInterface } from '~/src/types/forms/LoginTypeSelectionFormInterface'
import LoginTypeSelectionFormFields from '~/src/components/auth/login-type-selection/LoginTypeSelectionFormFields'
import LoginTypeSelectionFormActions from '~/src/components/auth/login-type-selection/LoginTypeSelectionFormActions'
import ValidatorUtils from '~/src/utils/ValidatorUtils'
import { useEoState } from '~/src/hooks/useEoState'
import AUTHENTICATION_FLOW_STATE, { AuthenticationFlowState } from '~/src/stores/authentication-flow'
import { default as parsePhoneNumber } from 'libphonenumber-js'
import useAuthenticationFlowDialogs from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import FormError from '~/src/components/error/FormError'
import { useEffect } from 'react'

interface Props {
  fieldsColor?: 'container' | 'content',
}

const _clearPhoneNumber = (value: string | null | undefined): string | null => {
  if (!value) {
    return null
  }

  return parsePhoneNumber(value)?.number || null
}

const LoginTypeSelectionForm = ({ fieldsColor = 'content' }: Props) => {
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { t } = useTranslations()
  const { next } = useAuthenticationFlowDialogs()

  const formik: FormikProps<LoginTypeSelectionFormInterface> = useFormik<LoginTypeSelectionFormInterface>({
    initialValues: {
      email: '',
      phone: ''
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .email(t('errors.emailInvalid'))
        .test('email-or-phone', t('errors.required'), function (value: string | undefined) {
          const { phone } = this.parent
          const phoneNumber = _clearPhoneNumber(phone)

          if (!value && !phoneNumber) {
            return this.createError({
              path: 'email',
              message: t('errors.required')
            })
          }

          if (value && phoneNumber) {
            return this.createError({
              path: 'email',
              message: t('errors.required')
            })
          }

          return true
        }),
      phone: yup
        .string()
        .test('phone-or-email', t('errors.required'), function (value: string | undefined) {
          const { email } = this.parent
          const phoneNumber = _clearPhoneNumber(value)

          if (!phoneNumber && !email) {
            return this.createError({
              path: 'phone',
              message: t('errors.required')
            })
          }

          if (phoneNumber && email) {
            return this.createError({
              path: 'phone',
              message: t('errors.required')
            })
          }

          if (phoneNumber && !ValidatorUtils.isPhoneNumberValid(value)) {
            return this.createError({
              path: 'phone',
              message: t('errors.phoneWrongFormat')
            })
          }

          return true
        })
    }),
    onSubmit: (values => {
      const email: string | null = values.email || null
      const phone: string | null = _clearPhoneNumber(values.phone) || null

      setAuthenticationFlowState((state: AuthenticationFlowState) => ({
        ...state,
        customer: {
          ...state.customer,
          email,
          phone
        }
      } as AuthenticationFlowState))

      next()
    })
  })

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      customer: {
        ...(state.customer),
        email: formik.values.email ?? undefined,
        phone: formik.values.phone ?? undefined
      }
    }))
  }, [ formik.values ])

  return (
    <>
      {authenticationFlowState.errors && <FormError errors={authenticationFlowState.errors}/>}

      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            mt: 2
          }}
        >
          <LoginTypeSelectionFormFields
            form={formik}
            fieldsColor={fieldsColor}/>

          <LoginTypeSelectionFormActions form={formik}/>
        </Box>
      </form>
    </>
  )
}

export default LoginTypeSelectionForm
